/**
 * Centralized booking action permissions
 * Determines which actions are available based on booking status and user role
 */

import * as localService from "../services/localService";

/**
 * Check if current user is a Platform Admin
 */
export const isCurrentUserPlatformAdmin = () => {
  const user = localService.getUser();
  if (!user) return false;
  return user?.role === "ROLE_ADMINISTRATOR" || user?.organizationRole?.code === "ROLE_ADMINISTRATOR";
};

/**
 * Check if a booking can be modified
 * Users can only modify bookings that are in PENDING status
 * After check-in, modifications are not allowed
 */
export const canModify = booking => {
  const status = booking?.status?.toUpperCase();

  // Only allow modifications for PENDING bookings
  // Do NOT allow modifications after check-in or for terminal statuses
  return status === "PENDING";
};

/**
 * Check if a booking can be checked in
 * Only PENDING bookings can be checked in
 */
export const canCheckIn = booking => {
  const status = booking?.status?.toUpperCase();
  // Allow check-in for PENDING (manual/direct), CONFIRMED, or PAID bookings
  return ["PENDING", "CONFIRMED", "PAID"].includes(status);
};

/**
 * Check if a booking can be checked out
 * Only CHECKED_IN bookings can be checked out
 */
export const canCheckOut = booking => {
  const status = booking?.status?.toUpperCase();
  return status === "CHECKED_IN";
};

/**
 * Check if payment can be made for a booking
 * Only PENDING_PAYMENT bookings can be paid
 * Platform Admins should never see the pay option
 */
export const canPayNow = booking => {
  // Platform Admins should never have payment options
  if (isCurrentUserPlatformAdmin()) {
    return false;
  }

  // Play services with OFFLINE-only ticket types should never show Pay Now.
  // Offline tickets are fulfilled via a redirect URL, not an online payment.
  const isPlayService =
    booking?.isPlayService ||
    booking?.space?.category?.name === "Play" ||
    booking?.spaceInstance?.category?.name === "Play";

  if (isPlayService && booking?.tickets?.length > 0) {
    const allOffline = booking.tickets.every(t => t.ticketType === "OFFLINE");
    if (allOffline) {
      return false;
    }
  }

  const status = booking?.status?.toUpperCase();
  return status === "PENDING_PAYMENT";
};

/**
 * Check if a booking can be cancelled
 * Terminal statuses (CANCELLED, COMPLETED, NO_SHOW) cannot be cancelled
 * CHECKED_IN bookings should be checked out instead of cancelled
 */
export const canCancel = booking => {
  const status = booking?.status?.toUpperCase();

  // If no status, default to non-cancellable for safety or allow?
  // For now, let's keep it consistent: only allow cancellation for non-terminal statuses
  if (!status) return false;

  // Define terminal statuses that cannot be cancelled
  const terminalStatuses = ["CANCELLED", "CANCELED", "COMPLETED", "NO_SHOW"];

  // Also prevent cancellation of checked-in bookings (they should be checked out)
  const nonCancellableStatuses = [...terminalStatuses, "CHECKED_IN"];

  return !nonCancellableStatuses.includes(status);
};

/**
 * Check if a booking can be viewed
 * View is always available for all booking statuses
 */
export const canView = _booking => {
  return true;
};

/**
 * Check if any actions are available for a booking
 * Used to determine if the action menu should be shown
 */
export const hasAnyActions = booking => {
  return canModify(booking) || canCheckIn(booking) || canCheckOut(booking) || canPayNow(booking) || canCancel(booking);
};

/**
 * Check if a booking is in a terminal status
 * Terminal statuses are final states that cannot be changed
 */
export const isTerminalStatus = booking => {
  const status = booking?.status?.toUpperCase();
  const terminalStatuses = ["CANCELLED", "CANCELED", "COMPLETED", "NO_SHOW"];
  return terminalStatuses.includes(status);
};

/**
 * Get a human-readable explanation of what actions are available
 * Useful for displaying to users why certain actions are disabled
 */
export const getActionExplanation = booking => {
  const status = booking?.status?.toUpperCase();

  switch (status) {
    case "PENDING":
      return "You can modify, check in, or cancel this booking.";
    case "PENDING_PAYMENT":
      return "Please complete payment to proceed with this booking.";
    case "CHECKED_IN":
      return "Guest is currently checked in. You can only check out.";
    case "COMPLETED":
      return "This booking has been completed. No further actions are available.";
    case "CANCELLED":
    case "CANCELED":
      return "This booking has been cancelled. No further actions are available.";
    case "NO_SHOW":
      return "Guest did not show up for this booking. No further actions are available.";
    default:
      return "Available actions depend on the booking status.";
  }
};
